<template>
  <BasePopup
    v-if="displayPopup"
    class="bg-action-default-hi mood-tangaroa rounded-lg"
    :displayPopup
    @closed="handleClose"
  >
    <AppDownload
      v-if="displayAppDownload"
      :description="i18n(translations.appPopupDescription)"
      :termsDescription="
        i18n(translations.appPopupTermsDescription, {
          logMissingTranslation: false,
        })
      "
      :termsTitle="
        i18n(translations.appPopupTermsTitle, { logMissingTranslation: false })
      "
      :title="i18n(translations.appPopupTitle)"
      tracking-zone="app_download_notification"
      @actioned="handleState(STATE.ACTIONED)"
    />

    <EmailCollector
      v-if="displayEmailCollector"
      source="popup"
      @actioned="handleState(STATE.ACTIONED)"
    />
  </BasePopup>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import { POPUP_TYPE } from './LeadCollectionPopup.constants'
import translations from './LeadCollectionPopup.translations'
import AppDownload from './components/AppDownload/AppDownload.vue'
import BasePopup from './components/BasePopup/BasePopup.vue'
import EmailCollector from './components/EmailCollector/EmailCollectorAdapter.vue'
import { useLeadCollectionPopup } from './composables/useLeadCollectionPopup'
import { STATE, type StateValue, recordState } from './utils/popupPersistence'

const i18n = useI18n()
const { activePopup, routeName } = useLeadCollectionPopup()
const tracking = useTracking()

const displayPopup = ref(false)

const hasActivePopup = computed(() => {
  return !!activePopup.value
})

const displayAppDownload = computed(() => {
  return activePopup.value === POPUP_TYPE.APP_DOWNLOAD
})

const displayEmailCollector = computed(() => {
  return activePopup.value === POPUP_TYPE.EMAIL_COLLECTOR
})

function handleState(state: StateValue) {
  if (!activePopup.value) {
    return
  }

  recordState(activePopup.value, state)
  displayPopup.value = false
}

function handleClose() {
  handleState(STATE.CLOSED)

  const zone = displayAppDownload.value
    ? 'app_download_notification'
    : 'email_collection_notification'

  tracking.trackClick({
    zone,
    name: 'closed',
  })
}

function trackImpression() {
  if (!hasActivePopup.value) {
    return
  }

  const zone = displayAppDownload.value
    ? 'app_download_notification'
    : 'email_collection_notification'

  tracking.trackPromptNotificationImpression({
    zone,
    page_type: routeName,
  })
}

watch(
  () => hasActivePopup.value,
  (hasPopup) => {
    displayPopup.value = hasPopup

    if (hasPopup) {
      trackImpression()
    }
  },
  { immediate: true },
)
</script>
